<template>
  <main-header />
  <section class="success-section">
    <div class="container">
      <div class="success-card" v-if="!loading">
        <div class="success-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        
        <h1>Order Confirmed!</h1>
        <p class="success-message">Thank you for your purchase. Your order has been successfully placed.</p>
        
        <!-- Full order details -->
        <div class="order-details" v-if="order">
          <h2>Order Summary</h2>
          
          <div class="order-info">
            <div class="info-row">
              <span class="label">Order Number:</span>
              <span class="value">#{{ orderNumber }}</span>
            </div>
            <div class="info-row">
              <span class="label">Order Date:</span>
              <span class="value">{{ orderDate }}</span>
            </div>
            <div class="info-row">
              <span class="label">Email:</span>
              <span class="value">{{ order.accountEmail || order.emailAddress }}</span>
            </div>
          </div>
          
          <div class="order-items" v-if="order.order && order.order.length > 0">
            <h3>Items Ordered</h3>
            <div class="item" v-for="(item, index) in order.order" :key="index">
              <div class="item-image">
                <img :src="getItemImage(item)" :alt="item.brand || item.name" />
              </div>
              <div class="item-details">
                <div class="item-name">{{ item.brand || item.name }}</div>
                <div class="item-meta">
                  <span v-if="item.size">Size: {{ item.size }}</span>
                  <span>Qty: {{ item.quantity || 1 }}</span>
                </div>
              </div>
              <div class="item-price">
                ${{ (item.price * (item.quantity || 1)).toFixed(2) }}
              </div>
            </div>
          </div>
          
          <div class="order-totals">
            <div class="total-row">
              <span>Subtotal:</span>
              <span>${{ subtotal.toFixed(2) }}</span>
            </div>
            <div class="total-row discount-row" v-if="couponDiscount > 0">
              <span>Coupon Discount{{ order.couponCode ? ` (${order.couponCode})` : '' }}:</span>
              <span class="discount-value">-${{ couponDiscount.toFixed(2) }}</span>
            </div>
            <div class="total-row discount-row" v-if="firstPurchaseDiscount > 0">
              <span>First Purchase (10%):</span>
              <span class="discount-value">-${{ firstPurchaseDiscount.toFixed(2) }}</span>
            </div>
            <div class="total-row">
              <span>Shipping ({{ shippingMethod === 'express' ? 'Express' : 'Standard' }}):</span>
              <span>{{ shipping === 0 ? 'FREE' : '$' + shipping.toFixed(2) }}</span>
            </div>
            <div class="total-row grand-total">
              <span>Total:</span>
              <span>${{ order.total }}</span>
            </div>
          </div>
          
          <div class="shipping-info" v-if="order.address && order.address.streetAddress">
            <h3>Shipping Address</h3>
            <p>{{ order.name }}</p>
            <p>{{ order.address.streetAddress }}</p>
            <p>{{ order.address.city }}, {{ order.address.state }} {{ order.address.zip }}</p>
            <p>{{ order.address.country }}</p>
          </div>
          
          <div class="next-steps">
            <h3>What's Next?</h3>
            <ul>
              <li>You'll receive an order confirmation email at <strong>{{ order.emailAddress }}</strong></li>
              <li>We'll send you shipping updates as your order is processed</li>
              <li>Track your order anytime in your account</li>
            </ul>
          </div>
        </div>
        
        <div class="actions">
          <button class="btn-primary" @click="goToAccount">View My Orders</button>
          <button class="btn-secondary" @click="goToShop">Continue Shopping</button>
        </div>
      </div>
      
      <div class="loading-state" v-else>
        <div class="spinner"></div>
        <p>Loading your order details...</p>
      </div>
    </div>
  </section>
  <main-footer />
</template>

<script>
import MainHeader from "@/components/MainHeader.vue";
import MainFooter from "@/components/MainFooter.vue";
import { auth, db } from '@/firebase/init';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

export default {
  name: "OrderSuccessView",
  components: {
    MainHeader,
    MainFooter,
  },
  data() {
    return {
      loading: true,
      order: null,
      sessionId: null,
      unsubscribe: null, // Firestore listener cleanup
    };
  },
  computed: {
    orderNumber() {
      if (!this.order) return '';
      return this.order.checkoutSessionId?.substring(0, 12).toUpperCase() || 'N/A';
    },
    orderDate() {
      if (!this.order || !this.order.timeOrderedTs) return '';
      return new Date(this.order.timeOrderedTs).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    subtotal() {
      if (!this.order) return 0;
      // Use stored subtotal if available
      if (this.order.subtotal) {
        return parseFloat(this.order.subtotal);
      }
      // Fallback to calculating from items
      if (!this.order.order) return 0;
      return this.order.order.reduce((sum, item) => {
        return sum + (item.price * (item.quantity || 1));
      }, 0);
    },
    discount() {
      if (!this.order) return 0;
      // Use stored total discount if available
      if (this.order.totalDiscount) {
        return parseFloat(this.order.totalDiscount);
      }
      // Fallback: estimate from difference
      const total = parseFloat(this.order.total);
      return Math.max(0, this.subtotal + this.shipping - total);
    },
    couponDiscount() {
      if (!this.order || !this.order.couponDiscount) return 0;
      return parseFloat(this.order.couponDiscount);
    },
    firstPurchaseDiscount() {
      if (!this.order || !this.order.firstPurchaseDiscount) return 0;
      return parseFloat(this.order.firstPurchaseDiscount);
    },
    shipping() {
      if (!this.order) return 0;
      // Use stored shipping fee if available
      if (this.order.shippingFee) {
        return parseFloat(this.order.shippingFee);
      }
      // Fallback to default
      return 0;
    },
    shippingMethod() {
      if (!this.order || !this.order.shippingMethod) return 'standard';
      return this.order.shippingMethod;
    }
  },
  methods: {
    getItemImage(item) {
      // Check uploadedImageUrls first (from personalization flow)
      if (item.uploadedImageUrls && item.uploadedImageUrls.length > 0) {
        const firstImage = item.uploadedImageUrls[0];
        if (typeof firstImage === 'string') return firstImage;
        if (firstImage.url) return firstImage.url;
      }
      // Then check imageUrls (generic field)
      if (item.imageUrls && item.imageUrls.length > 0) {
        const firstImage = item.imageUrls[0];
        if (typeof firstImage === 'string') return firstImage;
        if (firstImage.url) return firstImage.url;
      }
      // Then check uploadedImages (legacy field)
      if (item.uploadedImages && item.uploadedImages.length > 0) {
        const firstImage = item.uploadedImages[0];
        if (typeof firstImage === 'string') return firstImage;
        if (firstImage.url) return firstImage.url;
      }
      // Finally check images array
      if (item.images && item.images.length > 0) {
        const firstImage = item.images[0];
        if (typeof firstImage === 'string' && firstImage.startsWith('http')) {
          return firstImage;
        }
        if (firstImage.url) return firstImage.url;
      }
      return require('@/assets/images/empty-cart.svg');
    },
    subscribeToOrder() {
      const user = auth.currentUser;
      if (!user) {
        console.error('User not authenticated');
        this.$router.push('/login');
        return;
      }

      this.sessionId = this.$route.query.session_id;
      
      if (!this.sessionId) {
        console.error('No session ID in URL');
        this.$router.push('/account');
        return;
      }

      console.log('🔔 Subscribing to orders for session:', this.sessionId);

      // Set up real-time listener for orders
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('accountUid', '==', user.uid),
        where('orderPaid', '==', true),
      );

      // Subscribe to real-time updates
      this.unsubscribe = onSnapshot(q, async (snapshot) => {
        console.log('📦 Orders snapshot received, docs:', snapshot.size);
        
        // Find order matching this session ID
        let foundOrder = null;
        snapshot.forEach((doc) => {
          const orderData = doc.data();
          const orderSessionId = orderData.checkoutSessionId || 
                                 orderData.checkoutSessionDocId || '';
          
          if (orderSessionId.includes(this.sessionId) || 
              this.sessionId.includes(orderSessionId)) {
            foundOrder = { id: doc.id, ...orderData };
          }
        });

        if (foundOrder && !this.order) {
          // Order found for the first time
          console.log('✅ Order found:', foundOrder.id);
          this.order = foundOrder;
          this.loading = false;
          
          // Clear cart and journeys after successful order
          await this.$store.dispatch('clear_cart_and_journeys');
          
          // Clear saved checkout state from localStorage
          localStorage.removeItem('checkout_state');
          console.log('🗑️ Cart and checkout state cleared');
          
          // Clean URL to remove session_id
          window.history.replaceState({}, document.title, '/order-success');
          
          // Unsubscribe after finding the order
          if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
          }
        } else if (foundOrder && this.order) {
          // Order updated
          this.order = foundOrder;
        }
      }, (error) => {
        console.error('Error listening to orders:', error);
        this.loading = false;
      });
    },
    goToAccount() {
      this.$router.push('/account');
    },
    goToShop() {
      this.$router.push('/shop');
    }
  },
  mounted() {
    this.subscribeToOrder();
  },
  beforeUnmount() {
    // Clean up listener when component is destroyed
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
};
</script>

<style scoped>
.success-section {
  min-height: 80vh;
  padding: 60px 20px;
  background: linear-gradient(135deg, #fffef9 0%, #fff5f0 100%);
}

.container {
  max-width: 900px;
  margin: 0 auto;
}

.success-card {
  background: white;
  border-radius: 20px;
  padding: 50px 40px;
  box-shadow: 0 8px 32px rgba(227, 114, 103, 0.15);
  border: 2px solid #e37267;
  text-align: center;
}

.success-icon {
  width: 90px;
  height: 90px;
  margin: 0 auto 30px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.5s ease-out;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
}

.success-icon svg {
  width: 50px;
  height: 50px;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

h1 {
  font-size: 3.2rem;
  color: #1b1b1b;
  margin-bottom: 12px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.success-message {
  font-size: 1.7rem;
  color: #666;
  margin-bottom: 40px;
  line-height: 1.6;
}

.order-details {
  text-align: left;
  margin-top: 40px;
  padding-top: 40px;
  border-top: 2px solid #e6d1c7;
}

.order-details h2 {
  font-size: 2.4rem;
  color: #e37267;
  margin-bottom: 24px;
  font-weight: 700;
}

.order-details h3 {
  font-size: 1.9rem;
  color: #1b1b1b;
  margin: 32px 0 16px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.order-info {
  background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
  padding: 24px;
  border-radius: 16px;
  margin-bottom: 32px;
  border: 2px solid #e6d1c7;
  box-shadow: 0 4px 12px rgba(227, 114, 103, 0.08);
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(230, 209, 199, 0.5);
  font-size: 1.5rem;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  color: #666;
  font-weight: 500;
}

.info-row .value {
  color: #1b1b1b;
  font-weight: 600;
}

.order-items {
  margin-bottom: 32px;
}

.item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
  border-radius: 16px;
  margin-bottom: 16px;
  border: 2px solid #e6d1c7;
  transition: all 0.3s ease;
}

.item:hover {
  box-shadow: 0 4px 16px rgba(227, 114, 103, 0.15);
  transform: translateY(-2px);
}

.item-image {
  width: 90px;
  height: 90px;
  flex-shrink: 0;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  border: 2px solid #e6d1c7;
}

.item-details {
  flex: 1;
}

.item-name {
  font-size: 1.7rem;
  font-weight: 700;
  color: #1b1b1b;
  margin-bottom: 6px;
}

.item-meta {
  display: flex;
  gap: 16px;
  font-size: 1.4rem;
  color: #666;
  font-weight: 500;
}

.item-price {
  font-size: 1.9rem;
  font-weight: 700;
  color: #10b981;
}

.order-totals {
  background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
  padding: 24px;
  border-radius: 16px;
  margin-bottom: 32px;
  border: 2px solid #e6d1c7;
  box-shadow: 0 4px 12px rgba(227, 114, 103, 0.08);
}

.total-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  font-size: 1.6rem;
  color: #666;
}

.total-row span:last-child {
  color: #1b1b1b;
  font-weight: 600;
}

.total-row.grand-total {
  border-top: 2px solid #e37267;
  margin-top: 12px;
  padding-top: 16px;
  font-size: 2.2rem;
  font-weight: 700;
  color: #1b1b1b;
}

.total-row.grand-total span {
  color: #1b1b1b;
}

.total-row.discount-row {
  color: #10b981;
}

.total-row.discount-row .discount-value {
  color: #10b981;
  font-weight: 600;
}

.shipping-info {
  background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
  padding: 24px;
  border-radius: 16px;
  margin-bottom: 32px;
  border: 2px solid #e6d1c7;
  box-shadow: 0 4px 12px rgba(227, 114, 103, 0.08);
}

.shipping-info p {
  margin: 6px 0;
  color: #1b1b1b;
  line-height: 1.8;
  font-size: 1.5rem;
}

.next-steps {
  background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
  padding: 28px;
  border-radius: 16px;
  border-left: 4px solid #3b82f6;
  margin-bottom: 32px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.next-steps h3 {
  color: #1e40af;
  margin-top: 0;
  font-size: 1.9rem;
  font-weight: 700;
}

.next-steps ul {
  margin: 16px 0 0;
  padding-left: 24px;
}

.next-steps li {
  color: #1e40af;
  margin-bottom: 12px;
  line-height: 1.8;
  font-size: 1.5rem;
}

.actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  padding: 16px 40px;
  border-radius: 999px;
  font-size: 1.6rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  letter-spacing: 0.3px;
}

.btn-primary {
  background: linear-gradient(135deg, #e37267, #f4a89f);
  color: white;
  box-shadow: 0 4px 16px rgba(227, 114, 103, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(227, 114, 103, 0.4);
}

.btn-secondary {
  background: white;
  color: #e37267;
  border: 2px solid #e37267;
  box-shadow: 0 2px 8px rgba(227, 114, 103, 0.15);
}

.btn-secondary:hover {
  background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(227, 114, 103, 0.2);
}

.loading-state {
  text-align: center;
  padding: 100px 20px;
}

.spinner {
  width: 60px;
  height: 60px;
  margin: 0 auto 24px;
  border: 4px solid #e6d1c7;
  border-top-color: #e37267;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: #666;
  font-size: 1.7rem;
  font-weight: 500;
}

@media (max-width: 768px) {
  .success-section {
    padding: 20px 10px;
  }
  
  .success-card {
    padding: 30px 16px;
  }
  
  h1 {
    font-size: 2rem;
  }
  
  h2 {
    font-size: 1.8rem;
  }
  
  h3 {
    font-size: 1.5rem;
  }
  
  .order-info {
    padding: 16px;
  }
  
  .info-row {
    flex-direction: column;
    gap: 4px;
    padding: 10px 0;
    font-size: 1.3rem;
  }
  
  .info-row .label {
    font-size: 1.2rem;
  }
  
  .info-row .value {
    font-size: 1.3rem;
    word-break: break-all;
  }
  
  .actions {
    flex-direction: column;
  }
  
  .btn-primary,
  .btn-secondary {
    width: 100%;
    padding: 14px 24px;
    font-size: 1.4rem;
  }
  
  .item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
  }
  
  .item-image {
    width: 70px;
    height: 70px;
  }
  
  .item-name {
    font-size: 1.5rem;
  }
  
  .item-meta {
    font-size: 1.2rem;
  }
  
  .item-price {
    width: 100%;
    text-align: right;
    font-size: 1.6rem;
  }
  
  .order-totals {
    padding: 16px;
  }
  
  .total-row {
    font-size: 1.4rem;
  }
  
  .total-row.grand-total {
    font-size: 1.8rem;
  }
  
  .shipping-info {
    padding: 16px;
  }
  
  .shipping-info p {
    font-size: 1.3rem;
  }
  
  .next-steps {
    padding: 20px;
  }
  
  .next-steps h3 {
    font-size: 1.6rem;
  }
  
  .next-steps li {
    font-size: 1.3rem;
  }
}
</style>
